import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';

import {
  buildActorIdentityColumn,
  buildActorParentColumn,
  mapActorCapabilityDetailField,
  mapActorDisplayDetailFields,
  renderActorCapabilityBadge,
  renderActorEnabledBadge,
} from '../channel-actors/display';
import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const channelEntityStatus = {
  [CoreEnums.ChannelEntityStatus.Active]: { status: 'success' },
  [CoreEnums.ChannelEntityStatus.Suspended]: { status: 'warning' },
  [CoreEnums.ChannelEntityStatus.Terminated]: { status: 'error' },
  [CoreEnums.ChannelEntityStatus.PendingApproval]: { status: 'processing' },
  [CoreEnums.ChannelEntityStatus.Rejected]: { status: 'rejected' },
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelEntityStatus, 'Channel.Actors'),
  enumSource: CoreEnums.ChannelEntityStatus,
  options: createEnumOptions(CoreEnums.ChannelEntityStatus, 'channelEntityStatus', {
    casing: 'camel',
  }),
  badgeMap: channelEntityStatus,
  badgeType: 'group-item',
};

const CompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'company',
  label: 'global.text.company',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'id',
};

const OwnerCompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'ownerCompany',
  label: 'global.text.ownerCompany',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'ownerCompanyId',
};

const CanCreatePartnersFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'canCreatePartners',
  label: 'global.text.canCreatePartners',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
};

const CanCreateResellersFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'canCreateResellers',
  label: 'global.text.canCreateResellers',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
};

const CanCreateCustomersFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'canCreateCustomers',
  label: 'global.text.canCreateCustomers',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
};

const PartnerAgreementModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'partnerAgreementMode',
  label: 'global.text.partnerAgreementMode',
  enumNamespace: getEnumNamespace(CoreEnums.DistributorPartnerAgreementMode, 'Channel.Actors'),
  enumSource: CoreEnums.DistributorPartnerAgreementMode,
  options: createEnumOptions(
    CoreEnums.DistributorPartnerAgreementMode,
    'distributorPartnerAgreementMode',
    { casing: 'camel' },
  ),
};

const IsActiveFilter = {
  ...Filters.IsActiveFilter,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  StatusFilter,
  CompanyFilter,
  OwnerCompanyFilter,
  CanCreatePartnersFilter,
  CanCreateResellersFilter,
  CanCreateCustomersFilter,
  PartnerAgreementModeFilter,
  IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for status / isActive / canCreate*.
 * Other enum selects use `_form.js` locale options.
 */
export function mapChannelDistributorDetailFields(rows, { item }) {
  const mapped = mapActorDisplayDetailFields(rows, item);
  if (!item || !mapped?.length) return mapped;

  return mapped.map((row) => {
    if (row.name === 'status') {
      const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelEntityStatus, item.status);
      const config = resolved !== undefined ? channelEntityStatus[resolved] : null;
      return {
        ...row,
        badgeStatus: config?.status || 'default',
        badgeType: 'group-item',
      };
    }

    if (['canCreatePartners', 'canCreateResellers', 'canCreateCustomers'].includes(row.name)) {
      return mapActorCapabilityDetailField(row, item, row.name);
    }

    return row;
  });
}

export function buildChannelDistributorColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      width: 120,
      render: (_, record) => renderActorEnabledBadge(record, formatMessage),
    },
    {
      key: 'company/commercialName',
      title: formatMessage({ id: 'global.text.company' }),
      dataIndex: ['company', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    buildActorIdentityColumn({ formatMessage, highlight }),
    {
      key: 'ownerCompany/commercialName',
      title: formatMessage({ id: 'global.text.ownerCompany' }),
      dataIndex: ['ownerCompany', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    buildActorParentColumn({ formatMessage, highlight }),
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelEntityStatus, value);
        const config = resolved !== undefined ? channelEntityStatus[resolved] : null;
        if (!config) {
          return <Badge status="default" type="group-item" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status={config.status}
            type="group-item"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.ChannelEntityStatus,
                resolved,
                'channelEntityStatus',
              ),
            })}
          />
        );
      },
    },
    {
      key: 'canCreatePartners',
      title: formatMessage({ id: 'global.text.canCreatePartners' }),
      dataIndex: 'canCreatePartners',
      align: 'center',
      sorter: true,
      render: (_, record) => renderActorCapabilityBadge(record, 'canCreatePartners', formatMessage),
    },
    {
      key: 'canCreateResellers',
      title: formatMessage({ id: 'global.text.canCreateResellers' }),
      dataIndex: 'canCreateResellers',
      align: 'center',
      sorter: true,
      render: (_, record) => renderActorCapabilityBadge(record, 'canCreateResellers', formatMessage),
    },
    {
      key: 'canCreateCustomers',
      title: formatMessage({ id: 'global.text.canCreateCustomers' }),
      dataIndex: 'canCreateCustomers',
      align: 'center',
      sorter: true,
      render: (_, record) => renderActorCapabilityBadge(record, 'canCreateCustomers', formatMessage),
    },
    {
      key: 'partnerAgreementMode',
      title: formatMessage({ id: 'global.text.partnerAgreementMode' }),
      dataIndex: 'partnerAgreementMode',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.DistributorPartnerAgreementMode,
          value,
        );
        if (resolved === undefined) {
          return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status="default"
            type="process"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.DistributorPartnerAgreementMode,
                resolved,
                'distributorPartnerAgreementMode',
              ),
            })}
          />
        );
      },
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(record.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
